import { closeAllPanels, removeChildren, showAlert } from './uiUtils';
import { getHeartIcon, toggleToolContainer } from './toolsUI';
import { NCNode, SearchEntry, SearchMode } from '../../../../src/commonTypes';
import { resolveSearchSourceNode } from '../../../../src/services/board/searchSourceNode';
import { dedupeSearchEntries } from '../../../../src/services/board/searchResultDedupe';
import tippy, { followCursor, hideAll, roundArrow } from 'tippy.js';
import { InspireGraph } from '../graphs';
import {
	saveBoardImageFromUrl,
	search as requestBoardSearch,
	uploadBoardImage,
} from '../apiRequests';
import { clearFlowyHighlight, highlightFlowyResult, highlightFlowyTarget } from './flowyHighlight';

type BackendSearchResult = {
	id?: string;
	type?: 'article' | 'image';
	title?: string;
	snippet?: string;
	label?: string;
	url?: string;
	source?: string;
	platform?: string;
	imageUrl?: string;
	thumbnailUrl?: string;
	contextUrl?: string;
	lensId?: string;
	lensLabel?: string;
	searchTopic?: string;
};

const SOCIAL_PLATFORM_BADGES: Record<string, { label: string; modifier: string; title: string }> = {
	x: { label: 'X', modifier: 'x', title: 'X (Twitter)' },
	twitter: { label: 'X', modifier: 'x', title: 'X (Twitter)' },
	instagram: { label: 'IG', modifier: 'instagram', title: 'Instagram' },
	youtube: { label: 'YT', modifier: 'youtube', title: 'YouTube' },
	tiktok: { label: 'TT', modifier: 'tiktok', title: 'TikTok' },
	threads: { label: 'TH', modifier: 'threads', title: 'Threads' },
	reddit: { label: 'RD', modifier: 'reddit', title: 'Reddit' },
};

const resolveSourceBadge = (
	source?: string,
	platform?: string
): { className: string; label: string; title?: string } => {
	const sourceClass = (source || 'web').toLowerCase();
	if (sourceClass === 'social') {
		const platformKey = (platform || '').toLowerCase().trim();
		const platformBadge = SOCIAL_PLATFORM_BADGES[platformKey];
		if (platformBadge) {
			return {
				className: `source-badge source-badge--social source-badge--${platformBadge.modifier}`,
				label: platformBadge.label,
				title: `Social · ${platformBadge.title}`,
			};
		}
		return {
			className: 'source-badge source-badge--social',
			label: 'SOCIAL',
			title: 'Social listening',
		};
	}
	return {
		className: `source-badge source-badge--${sourceClass}`,
		label: sourceClass.toUpperCase(),
	};
};

type SearchResultDisplayOptions = {
	recommendedResultId?: string | null;
	recommendedResultIds?: string[];
	removedSensitive?: SearchEntry[];
	sensitiveFilteredCount?: number;
	sensitivityExplanation?: string;
	showFilteredSensitive?: boolean;
	deferredGdelt?: boolean;
	sourceDiagnostics?: Array<{
		source: string;
		status: 'ok' | 'empty' | 'failed';
		entries: number;
		error?: string;
	}>;
	availableLenses?: Array<{
		id: string;
		label: string;
		purpose: string;
		preferredSources: string[];
	}>;
	selectedLensId?: string;
	onLensSearch?: (lensId: string) => void;
};

const SEARCH_MODE_OPTIONS: Array<{ mode: SearchMode; label: string; requiresImage?: boolean }> = [
	{ mode: 'mixed', label: 'Mixed' },
	{ mode: 'text_only', label: 'Text only' },
	{ mode: 'image_only', label: 'Images only' },
	{ mode: 'visual_similarity', label: 'Visual similarity', requiresImage: true },
];

let activeSearchMode: SearchMode = 'mixed';
let rememberedSearchSource: NCNode | null = null;
let showFilteredSensitiveResults = false;
let lastRemovedSensitive: SearchEntry[] = [];
let lastSensitivityExplanation = '';
let lastSensitiveFilteredCount = 0;
let lastShownEntries: SearchEntry[] = [];
let lastShownNode: NCNode | null | undefined;
let activeLensId = 'all';
let pendingLensId = 'all';
let lastDisplayOptions: SearchResultDisplayOptions = {};
// A person may switch tabs while a Flowy search result is still in flight.
// Preserve their explicitly chosen result set instead of restoring stale mixed
// results when that older response arrives.
let preserveManualSearchResults = false;
let panelSearchRequestSequence = 0;
let currentPanelSearchRequest:
	| { id: number; sourceNodeUid: string; mode: SearchMode }
	| null = null;
const unusableImageResultUrls = new Set<string>();
type RetainedSearchImage = { blob: Blob; objectUrl: string };
const retainedSearchImages = new Map<string, RetainedSearchImage>();
let retainedSearchImageGeneration = 0;

const releaseRetainedSearchImage = (imageUrl: string): void => {
	const retained = retainedSearchImages.get(imageUrl);
	if (!retained) return;
	URL.revokeObjectURL(retained.objectUrl);
	retainedSearchImages.delete(imageUrl);
};

const clearRetainedSearchImages = (): void => {
	retainedSearchImageGeneration += 1;
	for (const retained of retainedSearchImages.values()) {
		URL.revokeObjectURL(retained.objectUrl);
	}
	retainedSearchImages.clear();
};

const retainedSearchImageFile = (imageUrl: string): File | null => {
	const retained = retainedSearchImages.get(imageUrl);
	if (!retained) return null;
	const mimeType = retained.blob.type.startsWith('image/') ? retained.blob.type : 'image/jpeg';
	const extension =
		mimeType.split('/')[1]?.replace('jpeg', 'jpg').replace(/[^a-z0-9]/gi, '') || 'jpg';
	return new File([retained.blob], `search-result.${extension}`, { type: mimeType });
};

/** Mark a direct panel request so a slower response cannot replace a newer tab/source. */
export const beginPanelSearchRequest = (node: NCNode, mode: SearchMode): number => {
	const id = ++panelSearchRequestSequence;
	currentPanelSearchRequest = { id, sourceNodeUid: node.uid, mode };
	unusableImageResultUrls.clear();
	return id;
};

export const isCurrentPanelSearchRequest = (
	requestId: number,
	node: NCNode,
	mode: SearchMode
): boolean =>
	currentPanelSearchRequest?.id === requestId &&
	currentPanelSearchRequest.sourceNodeUid === node.uid &&
	currentPanelSearchRequest.mode === mode &&
	activeSearchMode === mode;

/** Keep asynchronous Flowy guidance from moving selection away from an open search source. */
export const shouldPreserveSearchSourceSelection = (candidateNodeId: string): boolean => {
	const container = document.querySelector('#search-container');
	return Boolean(
		container &&
			!container.classList.contains('hidden') &&
			lastShownEntries.length > 0 &&
			rememberedSearchSource?.uid &&
			rememberedSearchSource.uid !== candidateNodeId
	);
};

const SEARCH_IMAGE_PLACEHOLDER = '/assets/search-result-placeholder.png';
const MIN_BOARD_IMAGE_SHORT_SIDE = 160;
const MIN_BOARD_IMAGE_PIXEL_AREA = 60_000;
const IMAGE_VALIDATION_TIMEOUT_MS = 12_000;
let searchResultsRenderSequence = 0;

const searchImagePlaybackUrl = (src: string): string =>
	src.startsWith('http') && !src.includes(window.location.host)
		? `/export-images?responseType=blob&url=${encodeURIComponent(src)}`
		: src;

const hasBoardUsableResolution = (image: HTMLImageElement): boolean =>
	Math.min(image.naturalWidth, image.naturalHeight) >= MIN_BOARD_IMAGE_SHORT_SIDE &&
	image.naturalWidth * image.naturalHeight >= MIN_BOARD_IMAGE_PIXEL_AREA;

const loadSearchResultImage = ({
	image,
	wrapper,
	thumbnailUrl,
	imageUrl,
	alt,
	requireUsableOriginal = false,
	onOriginalAvailable,
	onOriginalUnavailable,
}: {
	image: HTMLImageElement;
	wrapper: HTMLElement;
	thumbnailUrl?: string;
	imageUrl?: string;
	alt: string;
	requireUsableOriginal?: boolean;
	onOriginalAvailable?: () => void;
	onOriginalUnavailable?: () => void;
}): void => {
	const thumbnail = thumbnailUrl?.trim() || '';
	const fullResolution = imageUrl?.trim() || '';
	const fullResolutionPlayback = fullResolution ? searchImagePlaybackUrl(fullResolution) : '';
	const retentionGeneration = retainedSearchImageGeneration;
	let thumbnailLoaded = false;
	let thumbnailFailed = !thumbnail;
	let validationSettled = false;
	let validationTimer: number | null = null;
	const reportOriginalUnavailable = () => {
		if (validationSettled) return;
		validationSettled = true;
		if (validationTimer !== null) window.clearTimeout(validationTimer);
		onOriginalUnavailable?.();
	};
	const reportOriginalAvailable = () => {
		if (validationSettled) return;
		validationSettled = true;
		if (validationTimer !== null) window.clearTimeout(validationTimer);
		onOriginalAvailable?.();
	};
	if (requireUsableOriginal) {
		validationTimer = window.setTimeout(reportOriginalUnavailable, IMAGE_VALIDATION_TIMEOUT_MS);
	}

	const showUnavailable = () => {
		image.onerror = null;
		image.alt = 'Image preview unavailable';
		wrapper.classList.add('search-result-image-preview--unavailable');
		image.src = SEARCH_IMAGE_PLACEHOLDER;
	};

	const showFullResolution = (src = fullResolutionPlayback) => {
		image.onload = null;
		image.onerror = () => {
			if (requireUsableOriginal && !validationSettled) {
				reportOriginalUnavailable();
				return;
			}
			if (thumbnailLoaded) {
				image.onerror = null;
				image.src = thumbnail;
				wrapper.classList.remove('search-result-image-preview--high-resolution');
				return;
			}
			showUnavailable();
		};
		image.src = src;
		wrapper.classList.add('search-result-image-preview--high-resolution');
	};

	image.alt = alt;
	image.decoding = 'async';
	image.loading = 'lazy';

	if (thumbnail) {
		image.onload = () => {
			thumbnailLoaded = true;
			image.onload = null;
		};
		image.onerror = () => {
			thumbnailFailed = true;
			image.onload = null;
			image.onerror = null;
			if (!fullResolution && requireUsableOriginal) reportOriginalUnavailable();
			else if (fullResolution) showFullResolution();
			else showUnavailable();
		};
		image.src = thumbnail;
	} else if (!fullResolution) {
		if (requireUsableOriginal) reportOriginalUnavailable();
		else showUnavailable();
		return;
	} else {
		image.src = SEARCH_IMAGE_PLACEHOLDER;
	}
	if (!fullResolution) {
		if (requireUsableOriginal) reportOriginalUnavailable();
		return;
	}

	if (requireUsableOriginal) {
		const retained = retainedSearchImages.get(fullResolution);
		if (retained) {
			reportOriginalAvailable();
			showFullResolution(retained.objectUrl);
			return;
		}

		// Fetch the original once during validation and retain those exact bytes.
		// Clicking the result can then upload this Blob instead of asking the
		// backend to download the same remote image a second time.
		void fetch(fullResolutionPlayback, { cache: 'force-cache' })
			.then(async (response) => {
				if (!response.ok) throw new Error(`Image validation failed (${response.status})`);
				const blob = await response.blob();
				if (
					!blob.size ||
					(Boolean(blob.type) &&
						!blob.type.startsWith('image/') &&
						blob.type !== 'application/octet-stream')
				) {
					throw new Error('Image validation returned no usable image');
				}
				if (blob.size > 15 * 1024 * 1024) {
					throw new Error('Image exceeds the 15 MB import limit');
				}

				const objectUrl = URL.createObjectURL(blob);
				const fullResolutionPreload = new Image();
				fullResolutionPreload.decoding = 'async';
				fullResolutionPreload.onload = () => {
					if (
						validationSettled ||
						retentionGeneration !== retainedSearchImageGeneration ||
						!hasBoardUsableResolution(fullResolutionPreload)
					) {
						URL.revokeObjectURL(objectUrl);
						if (!validationSettled) reportOriginalUnavailable();
						return;
					}
					const alreadyRetained = retainedSearchImages.get(fullResolution);
					if (alreadyRetained) {
						URL.revokeObjectURL(objectUrl);
						reportOriginalAvailable();
						showFullResolution(alreadyRetained.objectUrl);
						return;
					}
					retainedSearchImages.set(fullResolution, { blob, objectUrl });
					reportOriginalAvailable();
					showFullResolution(objectUrl);
				};
				fullResolutionPreload.onerror = () => {
					URL.revokeObjectURL(objectUrl);
					reportOriginalUnavailable();
				};
				fullResolutionPreload.src = objectUrl;
			})
			.catch(() => reportOriginalUnavailable());
		return;
	}

	// Google thumbnails are intentionally small. Keep one as the immediate,
	// reliable preview, but validate the original through the same proxy path
	// used by board cards. Results that cannot become a usable board image are
	// removed instead of leaving a thumbnail-only card that fails after adding.
	const fullResolutionPreload = new Image();
	fullResolutionPreload.decoding = 'async';
	fullResolutionPreload.onload = () => {
		if (requireUsableOriginal && !hasBoardUsableResolution(fullResolutionPreload)) {
			reportOriginalUnavailable();
			return;
		}
		reportOriginalAvailable();
		showFullResolution();
	};
	fullResolutionPreload.onerror = () => {
		if (requireUsableOriginal) reportOriginalUnavailable();
		else if (thumbnailFailed) showUnavailable();
	};
	fullResolutionPreload.src = fullResolutionPlayback;
};

const renderSearchSourceStatus = (options: SearchResultDisplayOptions): void => {
	const container = document.querySelector('#search-container');
	const slot = container?.querySelector('.search-source-status-slot');
	container?.querySelector('.search-source-status')?.remove();
	if (!slot) return;
	const diagnostics = options.sourceDiagnostics || [];
	if (!diagnostics.length && !options.deferredGdelt) return;
	const diagnosticsBySource = new Map<
		string,
		{ source: string; status: 'ok' | 'empty' | 'failed'; entries: number; errors: string[] }
	>();
	for (const diagnostic of diagnostics) {
		const current = diagnosticsBySource.get(diagnostic.source) || {
			source: diagnostic.source,
			status: 'empty' as const,
			entries: 0,
			errors: [],
		};
		current.entries += diagnostic.entries;
		if (diagnostic.status === 'ok') current.status = 'ok';
		else if (diagnostic.status === 'failed' && current.status !== 'ok') current.status = 'failed';
		if (diagnostic.error && !current.errors.includes(diagnostic.error)) {
			current.errors.push(diagnostic.error);
		}
		diagnosticsBySource.set(diagnostic.source, current);
	}
	const providerDiagnostics = [...diagnosticsBySource.values()];
	const wrapper = document.createElement('div');
	wrapper.className = 'search-source-status';
	const parts = providerDiagnostics.map((diagnostic) => {
		const name =
			diagnostic.source === 'social' ? 'Social listening' : diagnostic.source.toUpperCase();
		if (diagnostic.status === 'ok') return `${name}: ${diagnostic.entries}`;
		if (diagnostic.status === 'empty') return `${name}: no matches`;
		const error = diagnostic.errors.join('; ');
		const reason = error.includes('quota')
			? 'quota reached'
			: error.includes('not configured')
				? 'not configured'
				: 'unavailable';
		return `${name}: ${reason}`;
	});
	if (options.deferredGdelt) parts.push('GDELT: searching in background…');
	wrapper.textContent = parts.join(' · ');
	wrapper.title = providerDiagnostics
		.map(
			(diagnostic) => `${diagnostic.source}: ${diagnostic.errors.join('; ') || diagnostic.status}`
		)
		.join('\n');
	slot.append(wrapper);
};

const renderSearchLensFilter = (options: SearchResultDisplayOptions): void => {
	const container = document.querySelector('#search-container');
	if (!container) return;
	const slot = container.querySelector('.search-lens-filter-slot');
	container.querySelector('.search-lens-filter')?.remove();
	container.querySelector('.search-source-status')?.remove();
	const lenses = options.availableLenses || [];
	if (!slot || !lenses.length || !options.onLensSearch) return;
	activeLensId = options.selectedLensId || 'all';
	if (pendingLensId !== activeLensId && !lenses.some(({ id }) => id === pendingLensId)) {
		pendingLensId = activeLensId;
	}

	const wrapper = document.createElement('div');
	wrapper.className = 'search-lens-filter';
	const intro = document.createElement('p');
	intro.className = 'search-lens-filter__intro';
	intro.textContent =
		'Research lenses come from this board’s engine and project focus. Choose a lens and search again:';
	const controls = document.createElement('div');
	controls.className = 'search-lens-filter__controls';
	const select = document.createElement('select');
	select.setAttribute('aria-label', 'Research lens');
	for (const lens of [
		{ id: 'all', label: 'All lenses', purpose: 'Search the primary lenses for this board.' },
		...lenses,
	]) {
		const option = document.createElement('option');
		option.value = lens.id;
		option.textContent = lens.label;
		option.title = lens.purpose;
		select.append(option);
	}
	select.value = pendingLensId;
	const button = document.createElement('button');
	button.type = 'button';
	button.textContent = 'Search again';
	button.disabled = pendingLensId === activeLensId;
	select.addEventListener('change', () => {
		pendingLensId = select.value;
		button.disabled = pendingLensId === activeLensId;
		const selected = lenses.find(({ id }) => id === pendingLensId);
		select.title = selected?.purpose || 'Search across the primary lenses for this board.';
	});
	button.addEventListener('click', () => {
		if (pendingLensId === activeLensId) return;
		button.disabled = true;
		options.onLensSearch?.(pendingLensId);
	});
	controls.append(select, button);
	wrapper.append(intro, controls);
	slot.append(wrapper);
};

export const getSearchModeForNode = (node: NCNode): SearchMode => {
	if (node.src) {
		return 'visual_similarity';
	}
	return activeSearchMode === 'visual_similarity' ? 'mixed' : activeSearchMode;
};

export const renderSearchModeToggle = (node: NCNode, onModeChange?: (mode: SearchMode) => void) => {
	const container = document.querySelector('#search-container')!;
	const toggleSlot = container.querySelector('.search-mode-toggle-slot');
	const existing = container.querySelector('.search-mode-toggle');
	existing?.remove();

	const availableOptions = SEARCH_MODE_OPTIONS.filter((option) =>
		option.requiresImage ? Boolean(node.src) : Boolean(node.label)
	);
	if (!availableOptions.length) return;

	const currentMode = getSearchModeForNode(node);
	activeSearchMode = currentMode;

	const wrapper = document.createElement('div');
	wrapper.className = 'search-mode-toggle';
	wrapper.setAttribute('role', 'radiogroup');
	wrapper.setAttribute('aria-label', 'Search mode');

	for (const option of availableOptions) {
		const button = document.createElement('button');
		button.type = 'button';
		button.dataset.searchMode = option.mode;
		button.innerText = option.label;
		button.classList.toggle('active', option.mode === currentMode);
		button.setAttribute('aria-pressed', String(option.mode === currentMode));
		button.addEventListener('click', () => {
			if (activeSearchMode === option.mode) return;
			activeSearchMode = option.mode;
			wrapper.querySelectorAll<HTMLButtonElement>('button').forEach((item) => {
				const isActive = item.dataset.searchMode === option.mode;
				item.classList.toggle('active', isActive);
				item.setAttribute('aria-pressed', String(isActive));
			});
			onModeChange?.(option.mode);
			// onModeChange starts the selected-tab request synchronously. Mark this
			// afterwards so that request's reset does not erase the user's choice.
			preserveManualSearchResults = true;
		});
		wrapper.append(button);
	}

	if (toggleSlot) {
		toggleSlot.append(wrapper);
	} else {
		container.insertBefore(wrapper, container.querySelector('.loading'));
	}
};

const renderSensitivityFilter = () => {
	const container = document.querySelector('#search-container');
	if (!container) return;
	const slot = container.querySelector('.sensitivity-filter-slot');
	const existing = container.querySelector('.sensitivity-filter');
	existing?.remove();
	if (!lastSensitiveFilteredCount && !lastRemovedSensitive.length) {
		return;
	}

	const wrapper = document.createElement('div');
	wrapper.className = 'sensitivity-filter';
	const summary = document.createElement('p');
	summary.className = 'sensitivity-filter__summary';
	summary.textContent =
		lastSensitivityExplanation ||
		`Filtered ${lastSensitiveFilteredCount} sensitive result${lastSensitiveFilteredCount === 1 ? '' : 's'}.`;
	const toggle = document.createElement('button');
	toggle.type = 'button';
	toggle.className = 'sensitivity-filter__toggle';
	toggle.setAttribute('aria-pressed', String(showFilteredSensitiveResults));
	toggle.textContent = showFilteredSensitiveResults
		? 'Hide filtered results'
		: `Show ${lastSensitiveFilteredCount || lastRemovedSensitive.length} filtered`;
	toggle.addEventListener('click', () => {
		showFilteredSensitiveResults = !showFilteredSensitiveResults;
		showSearchResults(lastShownEntries, lastShownNode, {
			...lastDisplayOptions,
			removedSensitive: lastRemovedSensitive,
			sensitiveFilteredCount: lastSensitiveFilteredCount,
			sensitivityExplanation: lastSensitivityExplanation,
			showFilteredSensitive: showFilteredSensitiveResults,
		});
	});
	wrapper.append(summary, toggle);
	if (slot) {
		slot.append(wrapper);
	} else {
		container.insertBefore(wrapper, container.querySelector('.loading'));
	}
};

const getSearchResultId = (entry: SearchEntry | BackendSearchResult, index: number): string =>
	(entry as { id?: string }).id ||
	(entry as { imageUrl?: string }).imageUrl ||
	(entry as SearchEntry).image ||
	entry.url ||
	entry.title ||
	(entry as { label?: string }).label ||
	`search-result-${index}`;

function liveSearchSource(node: NCNode | null | undefined): NCNode | null {
	if (!node?.uid) return null;
	return globalThis.neuroCreate.graph?.getNode(node.uid) || null;
}

function resolveBackendSearchNode({
	selectedNodeIds,
	selectedNodeLabels,
	candidate,
}: {
	selectedNodeIds?: string[];
	selectedNodeLabels?: string[];
	candidate?: NCNode | null;
}): NCNode | null {
	const graph = globalThis.neuroCreate.graph;
	return rememberSearchSourceNode(
		resolveSearchSourceNode({
			nodes: graph?.nodes || [],
			selectedNodeIds,
			selectedNodeLabels,
			fallback: liveSearchSource(candidate) || liveSearchSource(rememberedSearchSource) || null,
		})
	);
}

export function rememberSearchSourceNode(node: NCNode | null | undefined): NCNode | null {
	const live = liveSearchSource(node);
	if (live) rememberedSearchSource = live;
	return live;
}

export const resetSearch = () => {
	searchResultsRenderSequence += 1;
	clearRetainedSearchImages();
	const container = document.querySelector('#search-container')!;
	const loadingIndicator = container.querySelector('.loading')!;
	const noResults = container.querySelector('.no-results')!;
	const resultsList = container.querySelector('.results-list')!;
	clearFlowyHighlight();
	removeChildren(resultsList);
	loadingIndicator.classList.remove('hidden');
	noResults.classList.add('hidden');
	showFilteredSensitiveResults = false;
	lastRemovedSensitive = [];
	lastSensitivityExplanation = '';
	lastSensitiveFilteredCount = 0;
	lastShownEntries = [];
	lastShownNode = null;
	activeLensId = 'all';
	pendingLensId = 'all';
	lastDisplayOptions = {};
	preserveManualSearchResults = false;
	container.querySelector('.sensitivity-filter')?.remove();
	container.querySelector('.search-lens-filter')?.remove();
};

export const showSearchResults = (
	entries: Array<SearchEntry>,
	node?: NCNode | null,
	options: SearchResultDisplayOptions = {}
) => {
	const renderId = ++searchResultsRenderSequence;
	const sourceNode =
		rememberSearchSourceNode(node) || resolveBackendSearchNode({ candidate: node });
	const container = document.querySelector('#search-container')!;
	const loadingIndicator = container.querySelector('.loading')!;
	const noResults = container.querySelector('.no-results')!;
	const resultsList = container.querySelector('.results-list')!;

	lastShownEntries = entries;
	lastShownNode = node || null;
	lastDisplayOptions = options;
	if (options.removedSensitive) lastRemovedSensitive = options.removedSensitive;
	if (typeof options.sensitiveFilteredCount === 'number') {
		lastSensitiveFilteredCount = options.sensitiveFilteredCount;
	}
	if (typeof options.sensitivityExplanation === 'string') {
		lastSensitivityExplanation = options.sensitivityExplanation;
	}
	if (typeof options.showFilteredSensitive === 'boolean') {
		showFilteredSensitiveResults = options.showFilteredSensitive;
	}
	renderSensitivityFilter();
	renderSearchLensFilter(options);
	renderSearchSourceStatus(options);

	removeChildren(resultsList);
	const displayEntries = (showFilteredSensitiveResults
		? dedupeSearchEntries([...entries, ...lastRemovedSensitive])
		: dedupeSearchEntries(entries)
	).filter((entry) => {
		if (!entry.type?.startsWith('image')) return true;
		const imageUrl = entry.image || entry.url || '';
		return !imageUrl || !unusableImageResultUrls.has(imageUrl);
	});
	const uniqueEntries = displayEntries;
	let pendingImageValidations = uniqueEntries.filter((entry) => {
		if (!entry.type?.startsWith('image')) return false;
		return Boolean(entry.image || entry.url);
	}).length;
	const revealStableResults = () => {
		if (renderId !== searchResultsRenderSequence) return;
		resultsList.classList.remove('results-list--preparing');
		loadingIndicator.classList.add('hidden');
		noResults.classList.toggle('hidden', Boolean(resultsList.querySelector('li')));
	};
	const settleImageValidation = (listItem: HTMLLIElement, imageUrl: string, usable: boolean) => {
		if (renderId !== searchResultsRenderSequence) return;
		if (!usable) {
			unusableImageResultUrls.add(imageUrl);
			listItem.remove();
		}
		pendingImageValidations = Math.max(0, pendingImageValidations - 1);
		if (pendingImageValidations === 0) revealStableResults();
	};
	if (pendingImageValidations > 0) {
		resultsList.classList.add('results-list--preparing');
		loadingIndicator.classList.remove('hidden');
		noResults.classList.add('hidden');
	} else {
		resultsList.classList.remove('results-list--preparing');
		loadingIndicator.classList.add('hidden');
		noResults.classList.toggle('hidden', uniqueEntries.length > 0);
	}
	uniqueEntries.forEach((entry, index) => {
		const listItem = document.createElement('li');
		const sourceClass = (entry.source || 'web').toLowerCase();
		listItem.classList.add(`search-result-item--${sourceClass}`);
		const resultId = getSearchResultId(entry, index);
		listItem.dataset.resultId = resultId;
		listItem.dataset.resultIndex = String(index);
		listItem.dataset.resultTitle = entry.title || '';
		listItem.dataset.resultSnippet = entry.snippet || '';
		const recommendedIds = new Set([
			...(options.recommendedResultId ? [options.recommendedResultId] : []),
			...(options.recommendedResultIds || []),
		]);
		if (recommendedIds.has(resultId)) {
			listItem.classList.add('flowy-recommended-result');
		}

		const makeSourceBadge = (): HTMLElement => {
			const resolved = resolveSourceBadge(entry.source, entry.platform);
			const badge = document.createElement('span');
			badge.className = resolved.className;
			badge.innerText = resolved.label;
			if (resolved.title) badge.title = resolved.title;
			return badge;
		};

		const makeLensBadge = (label: string): HTMLElement => {
			const badge = document.createElement('span');
			badge.className = 'lens-badge';
			badge.innerText = label;
			badge.title = `Research lens: ${label}`;
			return badge;
		};

		const imageUrl = entry.image || (entry.type === 'image' ? entry.url : '');
		if (entry.type === 'article') {
			if (entry.image) {
				listItem.classList.add('search-result-item--has-image');
				const imageWrap = document.createElement('div');
				imageWrap.className = 'search-result-image-preview';
				const img = document.createElement('img');
				loadSearchResultImage({
					image: img,
					wrapper: imageWrap,
					thumbnailUrl: entry.thumbnailUrl,
					imageUrl: entry.image,
					alt: entry.title || entry.snippet || 'Search result image',
				});
				imageWrap.append(img);
				const imageBadgeRow = document.createElement('div');
				imageBadgeRow.className = 'search-result-badges';
				imageBadgeRow.append(makeSourceBadge());
				if (entry.lensLabel) {
					imageBadgeRow.append(makeLensBadge(entry.lensLabel));
				}
				imageWrap.append(imageBadgeRow);
				listItem.append(imageWrap);
			}
			const searchResultDiv = document.createElement('div');
			searchResultDiv.className = 'search-result';
			if (!entry.image) {
				const badgeRow = document.createElement('div');
				badgeRow.className = 'search-result-badges';
				badgeRow.append(makeSourceBadge());
				if (entry.lensLabel) {
					badgeRow.append(makeLensBadge(entry.lensLabel));
				}
				searchResultDiv.append(badgeRow);
			}
			const title = document.createElement('div');
			title.className = 'title';
			if (entry.title) {
				title.innerText = entry.title;
			}
			searchResultDiv.append(title);

			const text = document.createElement('div');
			text.className = 'text';
			if (entry.snippet) {
				text.innerText = entry.snippet;
			}
			searchResultDiv.append(text);
			listItem.append(searchResultDiv);
		} else if (entry.type?.startsWith('image') && imageUrl) {
			listItem.classList.add('search-result-item--validating-image');
			listItem.setAttribute('aria-busy', 'true');
			listItem.classList.add('search-result-item--image-only');
			const imageWrap = document.createElement('div');
			imageWrap.className = 'search-result-image-preview';
			const img = document.createElement('img');
			loadSearchResultImage({
				image: img,
				wrapper: imageWrap,
				thumbnailUrl: entry.thumbnailUrl,
				imageUrl,
				alt: entry.title || entry.snippet || 'Search result image',
				requireUsableOriginal: true,
				onOriginalAvailable: () => {
					listItem.classList.remove('search-result-item--validating-image');
					listItem.removeAttribute('aria-busy');
					settleImageValidation(listItem, imageUrl, true);
				},
				onOriginalUnavailable: () => {
					settleImageValidation(listItem, imageUrl, false);
				},
			});
			imageWrap.append(img);
			const badgeRow = document.createElement('div');
			badgeRow.className = 'search-result-badges';
			badgeRow.append(makeSourceBadge());
			imageWrap.append(badgeRow);
			listItem.append(imageWrap);
			if (entry.title || entry.snippet) {
				const caption = document.createElement('div');
				caption.className = 'search-result-caption';
				caption.innerText = entry.title || entry.snippet || '';
				listItem.append(caption);
			}
		}

		const link = document.createElement('a');
		link.target = '_blank';
		const resultLink = entry.type === 'image' ? entry.contextUrl || entry.url : entry.url;
		if (resultLink) {
			link.href = resultLink;
		}
		link.innerText =
			entry.displayLink || (entry.type === 'image' ? 'image source' : entry.url) || 'Link';
		listItem.append(link);

		const textToSave = `${entry.title || ''}\n\n${entry.snippet || ''}`;
		const resultTopics = [
			entry.source ? `source:${entry.source}` : 'source:web',
			...(entry.source === 'social' && entry.platform
				? [`platform:${String(entry.platform).toLowerCase()}`]
				: []),
			...(entry.lensLabel ? [`lens:${entry.lensLabel}`] : []),
			...(entry.lensId ? [`lens-id:${entry.lensId}`] : []),
			...(entry.searchTopic ? [`topic:${entry.searchTopic}`] : []),
		];

		listItem.append(
			getHeartIcon(
				entry.type === 'image' ? 'search-image' : 'search-page',
				() => entry.title || entry.snippet || (entry.type === 'image' ? 'Visual reference' : null),
				entry.type === 'image' ? imageUrl : entry.url,
				textToSave,
				resultTopics
			)
		);

		if (neuroCreate.graph?.graphMode !== 'ideate') {
			// eslint-disable-next-line @typescript-eslint/no-misused-promises
			listItem.addEventListener('click', async (e) => {
				const { nodeName, parentElement } = e.target as HTMLElement;
				const isHeartIconClicked = nodeName === 'IMG' && parentElement?.classList.contains('heart');
				const isLink = nodeName === 'A';
				const graph = globalThis.neuroCreate.graph as InspireGraph;
				if (!isHeartIconClicked && !isLink && graph) {
					if (listItem.dataset.addingToBoard === 'true') return;
					listItem.dataset.addingToBoard = 'true';
					listItem.classList.add('search-result-item--adding');
					try {
						const linkTarget =
							rememberSearchSourceNode(sourceNode) || liveSearchSource(rememberedSearchSource);
						if (!linkTarget) {
							throw new Error('The source node for this search is no longer available');
						}
						let addedNode;
						if (entry.type === 'image') {
							// Add image to board
							const boardId = document.body.dataset.boardId!;
							const retainedFile = retainedSearchImageFile(imageUrl);
							const url = retainedFile
								? await uploadBoardImage(boardId, retainedFile)
								: await saveBoardImageFromUrl(boardId, imageUrl);
							const label = entry.title || entry.snippet || entry.displayLink || 'Visual reference';
							addedNode = graph.addSingleNode(
								linkTarget,
								label,
								undefined,
								resultTopics,
								[],
								url,
								false,
								entry.contextUrl || entry.url || undefined
							);
						} else {
							addedNode = graph.addSingleNode(
								linkTarget,
								textToSave,
								undefined,
								resultTopics,
								[],
								undefined,
								false,
								entry.url || undefined
							);
						}
						if (addedNode && entry.source === 'social') {
							addedNode.style = 'rect';
							addedNode.groundingEvidence = {
								source: `social-profile:${entry.platform || 'social'}`,
								title: (
									entry.content ||
									entry.snippet ||
									entry.title ||
									'Social observation'
								).slice(0, 140),
								detail: (entry.content || entry.snippet || entry.title || '').slice(0, 320),
								content: entry.content || entry.snippet,
								url: entry.url,
								image: entry.image,
								isVideo: entry.socialMetadata?.isVideo,
								platform: entry.platform,
								author: entry.author,
								publishedAt: entry.publishedAt,
								engagement: entry.engagement,
								sentiment: entry.nativeSentiment?.polarity as
									| 'positive'
									| 'neutral'
									| 'negative'
									| 'mixed'
									| undefined,
								emotions: entry.nativeSentiment?.dominantEmotion
									? [entry.nativeSentiment.dominantEmotion]
									: [],
							};
							neuroCreate.saveNodes?.update([addedNode]);
						}
						listItem.remove();
						if (entry.type === 'image') releaseRetainedSearchImage(imageUrl);
						clearFlowyHighlight();
					} catch (error) {
						const errorMessage = error instanceof Error ? error.message : String(error);
						console.error('[search][add-result-failed]', {
							message: errorMessage,
							resultType: entry.type,
							hasImageUrl: Boolean(imageUrl),
						});
						showAlert(
							errorMessage.startsWith('Session expired.')
								? 'Your session expired. Please refresh the page and sign in again.'
								: entry.type === 'image'
								? 'This image could not be added to the board. Please try another result.'
								: 'This search result could not be added to the board.'
						);
					} finally {
						delete listItem.dataset.addingToBoard;
						listItem.classList.remove('search-result-item--adding');
					}
				}
			});

			const tooltip = tippy(listItem, {
				theme: 'neuro',
				arrow: roundArrow,
				delay: 0,
				duration: 0,
				offset: [0, 30],
				placement: 'bottom',
				followCursor: true,
				content: 'Add to board',
				plugins: [followCursor],
			});
			listItem?.addEventListener('mouseover', (e) => {
				const { nodeName, parentElement } = e.target as HTMLElement;
				const isHeartIconHovered = nodeName === 'IMG' && parentElement?.classList.contains('heart');
				const isLink = nodeName === 'A';
				if (!isHeartIconHovered && !isLink) {
					tooltip.show();
				} else {
					hideAll();
				}
				return;
			});
		}

		resultsList.append(listItem);
	});

	const recommendedResultId = options.recommendedResultId;
	if (recommendedResultId) {
		window.setTimeout(() => {
			highlightFlowyResult({
				panelSelector: '#search-container',
				resultId: recommendedResultId,
				label: 'Flowy pick',
			});
		}, 80);
	}
};

export const openBackendSearchPanel = ({
	selectedNodeIds,
	selectedNodeLabels,
}: {
	selectedNodeIds?: string[];
	selectedNodeLabels?: string[];
}) => {
	const existingContainer = document.querySelector('#search-container');
	if (
		preserveManualSearchResults &&
		existingContainer &&
		!existingContainer.classList.contains('hidden') &&
		lastShownEntries.length > 0
	) {
		return;
	}
	const node = resolveBackendSearchNode({ selectedNodeIds, selectedNodeLabels });
	closeAllPanels('search-container');
	if (node) {
		toggleToolContainer('search', node);
	}
	document.querySelector('#search-container')?.classList.remove('hidden');
	resetSearch();
};

export const showBackendSearchResults = ({
	results,
	selectedNodeIds,
	selectedNodeLabels,
	recommendedResultId,
	recommendedResultIds,
	removedSensitiveCount,
	sensitivityExplanation,
	availableLenses,
	selectedLensId,
	sourceDiagnostics,
}: {
	results: BackendSearchResult[];
	selectedNodeIds?: string[];
	selectedNodeLabels?: string[];
	recommendedResultId?: string | null;
	recommendedResultIds?: string[];
	removedSensitiveCount?: number;
	sensitivityExplanation?: string;
	availableLenses?: SearchResultDisplayOptions['availableLenses'];
	selectedLensId?: string;
	sourceDiagnostics?: SearchResultDisplayOptions['sourceDiagnostics'];
}) => {
	const node = resolveBackendSearchNode({ selectedNodeIds, selectedNodeLabels });
	const existingContainer = document.querySelector('#search-container');
	const preserveVisibleResults = Boolean(
		preserveManualSearchResults &&
		lastShownNode &&
		existingContainer &&
		!existingContainer.classList.contains('hidden') &&
		lastShownEntries.length > 0
	);
	if (node && !preserveVisibleResults) {
		toggleToolContainer('search', node);
	}
	const backendEntries = results.map(
		(result): SearchEntry =>
			({
				type:
					result.type ||
					(result.imageUrl && !result.title && !result.snippet ? 'image' : 'article'),
				title: result.title || result.label || '',
				snippet: result.snippet || '',
				url: result.url || result.imageUrl || '',
				displayLink: result.source || result.url || '',
				source: result.source || 'web',
				...(result.platform ? { platform: result.platform } : {}),
				image: result.imageUrl || '',
				...(result.thumbnailUrl ? { thumbnailUrl: result.thumbnailUrl } : {}),
				...(result.contextUrl ? { contextUrl: result.contextUrl } : {}),
				...(result.lensId ? { lensId: result.lensId } : {}),
				...(result.lensLabel ? { lensLabel: result.lensLabel } : {}),
				...(result.searchTopic ? { searchTopic: result.searchTopic } : {}),
				...(result.id ? { id: result.id } : {}),
			}) as SearchEntry
	);
	const modeEntries = entriesForSearchMode(backendEntries, activeSearchMode);
	const entries = preserveVisibleResults ? lastShownEntries : modeEntries;
	const displayNode = preserveVisibleResults ? lastShownNode : node;
	const recommendations = visibleRecommendationIds(
		entries,
		recommendedResultId,
		recommendedResultIds
	);
	const rerunWithLens = (lensId: string) => {
		const boardId = document.body.dataset.boardId;
		if (!boardId || !displayNode?.label) return;
		resetSearch();
		void requestBoardSearch(
			boardId,
			displayNode.label,
			getSearchModeForNode(displayNode),
			undefined,
			lensId
		)
			.then((result) =>
				showSearchResults(result.entries, displayNode, {
					sensitiveFilteredCount: result.sensitiveFilteredCount,
					sensitivityExplanation: result.sensitivityExplanation,
					removedSensitive: result.removedSensitive,
					deferredGdelt: result.deferredGdelt,
					sourceDiagnostics: result.sourceDiagnostics,
					availableLenses: result.availableLenses,
					selectedLensId: result.selectedLensId,
					onLensSearch: rerunWithLens,
				})
			)
			.catch((error: unknown) => {
				console.error('[search][lens] request failed', error);
				showSearchResults([], displayNode);
			});
	};
	showSearchResults(entries, displayNode, {
		...recommendations,
		sensitiveFilteredCount: removedSensitiveCount || 0,
		sensitivityExplanation: sensitivityExplanation || '',
		removedSensitive: [],
		availableLenses,
		selectedLensId,
		sourceDiagnostics,
		onLensSearch: displayNode?.label ? rerunWithLens : undefined,
	});
};

const entriesForSearchMode = (entries: SearchEntry[], mode: SearchMode): SearchEntry[] => {
	if (mode === 'image_only' || mode === 'visual_similarity') {
		return entries.filter((entry) => entry.type === 'image');
	}
	if (mode === 'text_only') {
		return entries.filter((entry) => entry.type !== 'image');
	}
	return entries;
};

const visibleRecommendationIds = (
	entries: SearchEntry[],
	recommendedResultId?: string | null,
	recommendedResultIds?: string[]
): { recommendedResultId: string | null; recommendedResultIds: string[] } => {
	const visibleIds = new Set(entries.map(getSearchResultId));
	const candidates = [
		...(recommendedResultId ? [recommendedResultId] : []),
		...(recommendedResultIds || []),
	].filter((id, index, values) => visibleIds.has(id) && values.indexOf(id) === index);
	return {
		recommendedResultId: candidates[0] || null,
		recommendedResultIds: candidates.slice(0, 3),
	};
};

/** Merge a slow provider response into the currently displayed search. */
export const appendSearchResults = (
	entries: SearchEntry[],
	sourceNode: NCNode,
	sourceDiagnostics: SearchResultDisplayOptions['sourceDiagnostics'] = []
): void => {
	if (lastShownNode?.uid !== sourceNode.uid) return;
	showSearchResults([...lastShownEntries, ...entries], sourceNode, {
		...lastDisplayOptions,
		removedSensitive: lastRemovedSensitive,
		sensitiveFilteredCount: lastSensitiveFilteredCount,
		sensitivityExplanation: lastSensitivityExplanation,
		showFilteredSensitive: showFilteredSensitiveResults,
		deferredGdelt: false,
		sourceDiagnostics: [
			...(lastDisplayOptions.sourceDiagnostics || []).filter(({ source }) => source !== 'gdelt'),
			...(sourceDiagnostics || []),
		],
	});
};

export const highlightSearchResult = ({
	resultId,
	resultIds,
	resultIndex,
	label,
}: {
	resultId?: string | null;
	resultIds?: string[] | null;
	resultIndex?: number | null;
	label?: string | null;
}) => {
	const container = document.querySelector('#search-container');
	if (!container) return false;
	const normalizedLabel = (label || '').trim().toLowerCase();
	const resultIdSet = new Set([...(resultId ? [resultId] : []), ...(resultIds || [])]);
	const hasStableTarget = resultIdSet.size > 0 || Boolean(normalizedLabel);
	let matched = false;
	container.querySelectorAll<HTMLLIElement>('.results-list li').forEach((item) => {
		const title = (item.dataset.resultTitle || '').trim().toLowerCase();
		const snippet = (item.dataset.resultSnippet || '').trim().toLowerCase();
		const idMatches = Boolean(item.dataset.resultId && resultIdSet.has(item.dataset.resultId));
		const indexMatches = Boolean(
			typeof resultIndex === 'number' && item.dataset.resultIndex === String(resultIndex)
		);
		const labelMatches = Boolean(
			normalizedLabel &&
				(title === normalizedLabel ||
					snippet === normalizedLabel ||
					normalizedLabel.includes(title) ||
					normalizedLabel.includes(snippet) ||
					title.includes(normalizedLabel) ||
					snippet.includes(normalizedLabel))
		);
		// Index is only safe when no stable identity was supplied. Otherwise a
		// recommendation produced for an older Mixed result set can incorrectly
		// select the item at the same index in the Images tab.
		const isMatch = idMatches || labelMatches || (!hasStableTarget && indexMatches);
		item.classList.toggle('flowy-recommended-result', isMatch);
		if (isMatch && !matched) {
			matched = true;
			highlightFlowyTarget({
				target: item,
				label: 'Flowy pick',
			});
		}
	});
	return matched;
};
